import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { Observable, throwError } from "rxjs";
import { FilterOperation } from "src/app/models/custom-table/custom-table.model";
import { AppSessionService } from "../../app-session/app-session.service";
import {
  setSimpleFilterQuery,
  setAdvancedFilterQuery,
} from "src/app/shared/utils/tableUtils";
import { PaginationRequest } from "src/app/models/common/paginationRequest";
import { catchError, map, tap } from "rxjs/operators";

const EMPTY_STRING = "";

@Injectable({
  providedIn: "root",
})
export class ProductService {
  constructor(
    private http: HttpClient,
    private appSession: AppSessionService
  ) {}

  getProducts(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    const headers = this.setHeaders(
      filteringArgs,
      sortingArgs,
      advancedFilteringArgs
    );

    return this.http.get(
      this.buildDataUrl(
        paging,
        filteringArgs,
        sortingArgs,
        headers,
        "/search?"
      ),
      { headers }
    );
  }

  getProductSearch(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    const headers = this.setHeaders(
      filteringArgs,
      sortingArgs,
      advancedFilteringArgs
    );

    return this.http
      .get<any>(
        this.buildDataUrl(
          paging,
          filteringArgs,
          sortingArgs,
          headers,
          "/search?"
        ),
        { headers }
      )
      .pipe(
        map(({ result }) => result),
        catchError((error) => throwError(error))
      );
  }

  setHeaders(
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ) {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    let advancedFilterQuery = EMPTY_STRING;
    let orderQuery = EMPTY_STRING;

    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }

    if (advancedFilteringArgs) {
      advancedFilterQuery = setAdvancedFilterQuery(advancedFilteringArgs);
    }

    if (sortingArgs) {
      const { active, direction } = sortingArgs;
      orderQuery += active + "," + direction.toUpperCase();
    }

    if (filterQuery.length > 0 || advancedFilterQuery.length > 0) {
      headers = headers.append(
        "query",
        `${filterQuery} ${
          filterQuery.length > 0 && advancedFilterQuery.length > 0
            ? FilterOperation.AND
            : ""
        } ${advancedFilterQuery}`
      );
    }

    if (orderQuery.length > 0) {
      headers = headers.append("sort", orderQuery);
    }

    return headers;
  }

  getWarehouseProductList(
    paging?: any,
    filteringArgs?: any,
    sortingArgs?: any,
    advancedFilteringArgs?: any
  ): Observable<any> {
    const headers = this.setHeaders(
      filteringArgs,
      sortingArgs,
      advancedFilteringArgs
    );
    
    return this.http.get(
      this.buildDataUrl(paging, filteringArgs, sortingArgs, headers, "?"),
      { headers }
    );
  }

  createProduct(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.product);
    const url = `${this.appSession.baseUrl + "/api/v1/products"}`;
    return this.http.post(url, data, { headers });
  }

  updateProduct(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });
    const data = JSON.stringify(action.product);
    const url = `${this.appSession.baseUrl + "/api/v1/products"}/${
      action?.product.currCode
    }`;
    return this.http.put(url, data, { headers });
  }

  getProduct(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${this.appSession.baseUrl + "/api/v1/products"}/${code}`;
    return this.http.get(url, { headers });
  }

  getFamilyProducts(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/products/families"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }
    return this.http.get(url, { headers });
  }

  getGroupProducts(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/products/groups"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getUnitsProducts(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/productUnits"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getProductTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/products/productTypes"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  getBarcodeTypes(pageReq: PaginationRequest): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set(
      "Content-type",
      "application/x-www-form-urlencoded; "
    );

    if (pageReq && pageReq.query && (pageReq.query as string).length > 0) {
      headers = headers.set("query", pageReq.query);
    }

    const url = `${this.appSession.baseUrl + "/api/v1/products/barcodeTypes"}`;

    if (!pageReq) {
      // pageReq not defined
      pageReq = {
        offset: 0,
        limit: 999,
        realSize: false,
      };
    }

    return this.http.get(url, { headers });
  }

  printBarcodes(payload: { barcodeType: string; products: string[] }): Observable<any> {
    const headers = new HttpHeaders({
      'Content-type': 'application/json',
    });
  
    const url = `${this.appSession.baseUrl}/api/v1/reports/productLabel`;
    return this.http.put<any>(url, payload, { headers });
  }


  getProductSiteList(code): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteProducts/byProduct/${code}`
    }`;

    return this.http.get(url, { headers });
  }

  getSiteProductsList(siteCode: string, filteringArgs?: any): Observable<any> {
    let headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    let filterQuery = EMPTY_STRING;
    if (filteringArgs) {
      filterQuery = setSimpleFilterQuery(filteringArgs);
    }
  
    headers = headers.append("query", filterQuery);

    const url = `${
      this.appSession.baseUrl + `/api/v1/siteProducts/site/${siteCode}/search`
    }`;

    return this.http.get<any>(url, { headers }).pipe(
      map(({ result }) => result),
      catchError((error) => throwError(error))
    );
  }

  createProductSiteList(action): Observable<any> {
    const headers = new HttpHeaders({
      "Content-type": "application/json",
    });

    const { productCode, productList } = action;
    const data = JSON.stringify(productList);
    const url = `${this.appSession.baseUrl}/api/v1/siteProducts/byProduct/${productCode}`;

    return this.http.post(url, data, { headers });
  }

  private buildDataUrl(
    pagingArgs: any,
    filteringArgs: any,
    sortingArgs: any,
    header: HttpHeaders,
    path: string
  ): string {
    let baseQueryString = `${
      this.appSession.baseUrl + "/api/v1/products"
    }${path}`;
    let scrollingQuery = EMPTY_STRING;
    let query = EMPTY_STRING;

    if (pagingArgs) {
      scrollingQuery = this._buildScrollExpression(pagingArgs);
    }

    query +=
      scrollingQuery !== EMPTY_STRING ? `&${scrollingQuery}` : EMPTY_STRING;

    baseQueryString += query;

    return baseQueryString;
  }

  private _buildScrollExpression(pagingArgs): string {
    const skip = pagingArgs.skip;
    const top = pagingArgs.top;
    const realSize = pagingArgs.realSize;

    if (top && top !== null) {
      return `real_size=${!!realSize}&offset=${skip}&limit=${top}`;
    } else {
      return `real_size=${!!realSize}&offset=${skip}`;
    }
  }
}
